import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getList,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { BackOfficeSurveySettings, BackOfficeSurveySettingsDTO } from "./backOfficeSurveySettings";
import { RespondentColumnDetails, RespondentColumnDetailsDTO } from "../types/respondentColumnDetails";
/* eslint-disable import/export */
export abstract class UpdateSurveySettingsRPC implements
  LeoRPC<UpdateSurveySettingsRPC.Request, UpdateSurveySettingsRPC.Response, UpdateSurveySettingsRPC.Errors.Errors>
{
  abstract execute(
    request: UpdateSurveySettingsRPC.Request
  ): Promise<LeoRPCResult<UpdateSurveySettingsRPC.Response, UpdateSurveySettingsRPC.Errors.Errors>>;
}

export namespace UpdateSurveySettingsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    surveyId: string;
    projectId: string;
    backOfficeSurveySettings: BackOfficeSurveySettingsDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isUpdateSurveySettingsRPCRequest: boolean = true;
  
    readonly surveyId: LeoUUID;
    readonly projectId: LeoUUID;
    readonly backOfficeSurveySettings: BackOfficeSurveySettings;
  
  
    constructor(
    surveyId: LeoUUID, 
    projectId: LeoUUID, 
    backOfficeSurveySettings: BackOfficeSurveySettings
    ) {
      this.surveyId = surveyId;
      this.projectId = projectId;
      this.backOfficeSurveySettings = backOfficeSurveySettings;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const backOfficeSurveySettings = BackOfficeSurveySettings.fromDTO(getObject(dto, "backOfficeSurveySettings", InvalidRequestBackOfficeSurveySettingsError));
      return new Request(
        surveyId,
        projectId,
        backOfficeSurveySettings
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyId: this.surveyId.toDTO(),
          projectId: this.projectId.toDTO(),
          backOfficeSurveySettings: this.backOfficeSurveySettings.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `UpdateSurveySettingsRPC(surveyId='${this.surveyId}',projectId='${this.projectId}',backOfficeSurveySettings='${this.backOfficeSurveySettings}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    backOfficeSurveySettings: BackOfficeSurveySettingsDTO;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isUpdateSurveySettingsResponse: boolean = true;
  
    readonly backOfficeSurveySettings: BackOfficeSurveySettings;
  
  
    constructor(
    backOfficeSurveySettings: BackOfficeSurveySettings
    ) {
      this.backOfficeSurveySettings = backOfficeSurveySettings;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const backOfficeSurveySettings = BackOfficeSurveySettings.fromDTO(getObject(dto, "backOfficeSurveySettings", InvalidResponseBackOfficeSurveySettingsError));
      return new Response(
        backOfficeSurveySettings
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          backOfficeSurveySettings: this.backOfficeSurveySettings.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `UpdateSurveySettings(backOfficeSurveySettings='${this.backOfficeSurveySettings}')`;
  };
  

  export enum RPCError {
    InvalidSurveyId = "INVALID_SURVEY_ID",
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    InvalidSurveyName = "INVALID_SURVEY_NAME",
    SurveyNameNotUnique = "SURVEY_NAME_NOT_UNIQUE",
    DeletedRespondentColumn = "DELETED_RESPONDENT_COLUMN"
  }

  export namespace Errors {
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `UpdateSurveySettingsRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `UpdateSurveySettingsRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `UpdateSurveySettingsRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface InvalidSurveyNameDTO {
      code: string;
    }
    
    export class InvalidSurveyName extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyName: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyName;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyNameDTO {
        const dto: InvalidSurveyNameDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyName.prototype.toString = function toString(): string {
      return `UpdateSurveySettingsRPC.INVALID_SURVEY_NAME()`;
    };
    
    
    export interface SurveyNameNotUniqueDTO {
      code: string;
    }
    
    export class SurveyNameNotUnique extends LeoRPCError<RPCError> {
      readonly isSurveyNameNotUnique: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SurveyNameNotUnique;
        super(code);
      }
    
    
      toDTO(): SurveyNameNotUniqueDTO {
        const dto: SurveyNameNotUniqueDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SurveyNameNotUnique.prototype.toString = function toString(): string {
      return `UpdateSurveySettingsRPC.SURVEY_NAME_NOT_UNIQUE()`;
    };
    
    
    export interface DeletedRespondentColumnDTO {
      code: string;
      respondentColumnDetails: RespondentColumnDetailsDTO[];
    }
    
    export class DeletedRespondentColumn extends LeoRPCError<RPCError> {
      readonly isDeletedRespondentColumn: boolean = true;
    
      readonly respondentColumnDetails: RespondentColumnDetails[];
    
      constructor(
      respondentColumnDetails: RespondentColumnDetails[]
      ) {
        const code = RPCError.DeletedRespondentColumn;
        super(code);
        this.respondentColumnDetails = respondentColumnDetails;
      }
    
      static fromDTO(dto: DeletedRespondentColumnDTO): DeletedRespondentColumn {
        const respondentColumnDetails = getList<RespondentColumnDetailsDTO, RespondentColumnDetails>(
          dto,
          "respondentColumnDetails",
          RespondentColumnDetails.fromDTO,
          InvalidResponseDeletedRespondentColumnRespondentColumnDetailsError
        );
        return new DeletedRespondentColumn(
          respondentColumnDetails
        );
      }
    
      toDTO(): DeletedRespondentColumnDTO {
        const dto: DeletedRespondentColumnDTO = {
          code: this.code,
          respondentColumnDetails: this.respondentColumnDetails.map((e: RespondentColumnDetails) => { return e.toDTO() })
        };
        return dto;
      }
    
      }
    
    DeletedRespondentColumn.prototype.toString = function toString(): string {
      return `UpdateSurveySettingsRPC.DELETED_RESPONDENT_COLUMN(respondentColumnDetails='${this.respondentColumnDetails}')`;
    };
    

    export type Errors = InvalidSurveyId | InvalidProjectId | ProjectAlreadyArchived | InvalidSurveyName | SurveyNameNotUnique | DeletedRespondentColumn;
    export type ErrorsDTO = InvalidSurveyIdDTO | InvalidProjectIdDTO | ProjectAlreadyArchivedDTO | InvalidSurveyNameDTO | SurveyNameNotUniqueDTO | DeletedRespondentColumnDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.InvalidSurveyName:
          return new InvalidSurveyName();
        case RPCError.SurveyNameNotUnique:
          return new SurveyNameNotUnique();
        case RPCError.DeletedRespondentColumn:
          return DeletedRespondentColumn.fromDTO(dto as DeletedRespondentColumnDTO);
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyIdError extends UpdateSurveySettingsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestProjectIdError extends UpdateSurveySettingsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestBackOfficeSurveySettingsError extends UpdateSurveySettingsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestBackOfficeSurveySettingsError";
  }
}

export class InvalidResponseBackOfficeSurveySettingsError extends UpdateSurveySettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseBackOfficeSurveySettingsError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends UpdateSurveySettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseInvalidProjectIdError extends UpdateSurveySettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends UpdateSurveySettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseInvalidSurveyNameError extends UpdateSurveySettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyNameError";
  }
}

export class InvalidResponseSurveyNameNotUniqueError extends UpdateSurveySettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyNameNotUniqueError";
  }
}

export class InvalidResponseDeletedRespondentColumnError extends UpdateSurveySettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDeletedRespondentColumnError";
  }
}

export class InvalidResponseDeletedRespondentColumnRespondentColumnDetailsError extends InvalidResponseDeletedRespondentColumnError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDeletedRespondentColumnRespondentColumnDetailsError";
  }
}
